/**
 * useTweaks — runtime theme / density toggles.
 *
 * Per [[0004 - styling-vanilla-css-modules-and-radix]]: the design's
 * runtime-theming pattern uses `[data-theme]` and `[data-density]` attribute
 * selectors on `<html>`. This hook is the only thing that writes those
 * attributes; it also persists to localStorage so the choice survives reload.
 *
 * Aurora A4: theme/density are now **server-backed** on the `platform` scope.
 * localStorage stays the instant first-paint cache; the host (apps/web
 * `__root.tsx`) passes the server-resolved `platform` prefs via `serverPrefs`
 * (applied once when they first arrive) and an `onPersist` callback that
 * write-throughs each change to `PATCH /api/platform/preferences`. The hook
 * itself stays dependency-free — it never imports the web API client.
 *
 * Accent is no longer a tweak here: it's resolved per-app by `useAppAccent`
 * and set as inline `--accent*` vars on `<html>`. The picker moved to
 * Settings → Appearance.
 *
 * Tokens.css contains the variables for each combination — adding a new
 * theme/density value means adding entries there too.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type Theme = 'light' | 'dark';
export type Density = 'compact' | 'regular' | 'comfy';

export interface Tweaks {
  theme: Theme;
  density: Density;
}

export const TWEAK_DEFAULTS: Tweaks = {
  theme: 'light',
  density: 'regular',
};

const STORAGE_KEY = 'k_os_tweaks';

const THEMES: readonly Theme[] = ['light', 'dark'];
const DENSITIES: readonly Density[] = ['compact', 'regular', 'comfy'];

const isTheme = (v: unknown): v is Theme => THEMES.includes(v as Theme);
const isDensity = (v: unknown): v is Density => DENSITIES.includes(v as Density);

export interface UseTweaksOptions {
  /**
   * Server-resolved `platform`-scope prefs (theme / density). Applied **once**,
   * when they first arrive — localStorage is the instant first-paint cache; the
   * server is the source of truth and corrects it on hydrate. Unknown values
   * (e.g. `theme: 'system'`) are ignored so the local value is kept.
   */
  serverPrefs?: { theme?: string | null; density?: string | null } | null;
  /**
   * Called after a *local* change so the host can write-through to the server
   * (PATCH `platform` scope). Not called during server hydration.
   */
  onPersist?: (patch: Partial<Tweaks>) => void;
}

function readStorage(): Tweaks {
  if (typeof window === 'undefined') return TWEAK_DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return TWEAK_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Tweaks>;
    return {
      theme: isTheme(parsed.theme) ? parsed.theme : TWEAK_DEFAULTS.theme,
      density: isDensity(parsed.density) ? parsed.density : TWEAK_DEFAULTS.density,
    };
  } catch {
    return TWEAK_DEFAULTS;
  }
}

function writeStorage(t: Tweaks): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  } catch {
    // localStorage unavailable (private mode, quota) — keep state in memory;
    // we don't degrade the UI for that.
  }
}

function applyToHtml(t: Tweaks): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = t.theme;
  root.dataset.density = t.density;
  // NOTE (Aurora A2): `data-accent` is no longer written here. The accent is
  // resolved per-app and stamped as inline `--accent*` vars by `useAppAccent`,
  // which also owns the `data-accent` marker (the finance charts observe it).
}

export function useTweaks(
  options?: UseTweaksOptions,
): readonly [Tweaks, <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void] {
  const [tweaks, setState] = useState<Tweaks>(() => {
    const initial = readStorage();
    applyToHtml(initial);
    return initial;
  });

  // Keep the persist callback in a ref so `setTweak` stays referentially stable
  // even though the host passes a fresh closure each render.
  const onPersistRef = useRef(options?.onPersist);
  onPersistRef.current = options?.onPersist;

  // Re-apply attributes whenever any tweak changes. Persistence is in the
  // setter (one localStorage write per change rather than per render).
  useEffect(() => {
    applyToHtml(tweaks);
  }, [tweaks]);

  // Server hydration (A4): apply the `platform`-scope prefs the first time they
  // arrive. Only known values override the cache; this never calls onPersist.
  const hydratedRef = useRef(false);
  const sp = options?.serverPrefs;
  const spTheme = sp?.theme ?? null;
  const spDensity = sp?.density ?? null;
  useEffect(() => {
    if (hydratedRef.current || !sp) return;
    hydratedRef.current = true;
    const patch: Partial<Tweaks> = {};
    if (isTheme(spTheme)) patch.theme = spTheme;
    if (isDensity(spDensity)) patch.density = spDensity;
    if (Object.keys(patch).length === 0) return;
    setState((prev) => {
      const next = { ...prev, ...patch };
      writeStorage(next);
      return next;
    });
  }, [sp, spTheme, spDensity]);

  const setTweak = useCallback(
    <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => {
      setState((prev) => {
        const next = { ...prev, [key]: value };
        writeStorage(next);
        return next;
      });
      onPersistRef.current?.({ [key]: value } as Partial<Tweaks>);
    },
    [],
  );

  return [tweaks, setTweak] as const;
}
